import { Pending } from '@cluey/cluey-components';
import classNames from 'classnames';
import { isNull } from 'lodash';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';

import withHeadTag from '../../../common/HeadComponent';
import HubPageLayout from '../../../common/hub/HubPageLayout';
import { Loader } from '../../../components/Loader/Loader';
import PillButtons, { PillButtonOption } from '../../../components/PillButtons/PillButtons';
import { SkeletonLoader } from '../../../components/SkeletonLoader';
import { formatError } from '../../../util/errors';
import { useSearchParams } from '../../../util/hooks/useSearchParams';
import { ClueyPlus } from './ClueyPlus/Subscription';
import useSubscriptionContext, { withSubscriptionContextProvider } from './useSubscriptionContext';

const Subscriptions = () => {
  const location = useLocation<{ studentId?: string }>();
  const searchParams = useSearchParams();
  const {
    clueyPlus: { getSubscription, isLoading: isLoadingClueyPlus },
    banner: { show: showBanner, message: bannerMessage },
    student: {
      students,
      studentIsLoading,
      studentError,
      selectedStudent,
      setSelectedStudent,
      selectedStudentEnrolments,
    },
  } = useSubscriptionContext();

  const studentsList: PillButtonOption[] = useMemo(() => {
    if (!studentIsLoading) {
      return [...(students ?? [])].map((student) => {
        const showLastName = student.lastName && student.lastName.toLowerCase() !== '[not provided]';
        return {
          id: student.studentId,
          label: `${student.firstName} ${showLastName ? student.lastName : ''}`,
          subLabel: student.yearLevel,
        };
      });
    }
    return [];
  }, [students, studentIsLoading]);

  const incomingStudentId = searchParams.get('studentId') ?? location.state?.studentId;

  // Re-run whenever a new studentId arrives via navigation or the studentId query
  // param (e.g. the account switcher redirecting here for a different student) so it
  // takes priority over whatever is currently selected. Otherwise only seed a default
  // once, so manual PillButtons selections aren't clobbered by unrelated re-renders.
  useEffect(() => {
    if (studentIsLoading || !students?.length) {
      return;
    }
    if (incomingStudentId) {
      const incomingStudent = students.find((student) => student.studentId === incomingStudentId);
      if (incomingStudent) {
        setSelectedStudent(incomingStudent);
        return;
      }
    }
    if (!selectedStudent) {
      setSelectedStudent(students[0] ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [students, studentIsLoading, setSelectedStudent, incomingStudentId]);

  const error = studentError ? formatError(studentError) : undefined;

  const isLoading = studentIsLoading || isLoadingClueyPlus;

  return (
    <HubPageLayout noContainer showFooterBorder>
      <div className="w-full bg-orange-100 font-[Poppins]">
        <div className="relative w-full">
          <div className="flex flex-col gap-3">
            <Pending
              loading={isLoading}
              error={studentError ? formatError(studentError) : undefined}
              loader={
                <div className="flex flex-row gap-x-4 py-3">
                  <SkeletonLoader loading={isLoading} className="h-12 w-32 !rounded-full" />
                  <SkeletonLoader loading={isLoading} className="h-12 w-32 !rounded-full" />
                  <SkeletonLoader loading={isLoading} className="h-12 w-32 !rounded-full" />
                </div>
              }
            >
              <PillButtons
                options={studentsList}
                selectedId={selectedStudent?.studentId ?? null}
                titleClassName="font-semibold"
                selectedButtonClassName="bg-[#01220D] border-[#01220D] text-white"
                selectedSubtitleClassName="text-blood-orange-4"
                onSelect={(id) => {
                  setSelectedStudent(students?.find((student) => student.studentId === id) ?? null);
                }}
              />
            </Pending>
            {showBanner && <>{bannerMessage}</>}
            {isNull(getSubscription()) && selectedStudentEnrolments.length > 0 ? (
              <div
                className={classNames(
                  'mb-10 flex w-full flex-col rounded-3xl border-2 border-dashed border-grey-2 bg-white p-7 lg:flex-row lg:p-10'
                )}
              >
                <b className="text-lg font-semibold">No subscriptions yet</b>
              </div>
            ) : (
              <div
                className={classNames(
                  'mb-10 flex w-full flex-col gap-x-10 gap-y-6 rounded-3xl border border-grey-100 bg-white p-7 md:p-10 lg:flex-row',
                  isLoading && 'justify-center'
                )}
              >
                <Pending
                  loading={isLoading}
                  error={error}
                  loader={<Loader className="flex min-h-[100px] items-center justify-center" />}
                >
                  <ClueyPlus selectedStudent={selectedStudent} />
                </Pending>
              </div>
            )}
          </div>
        </div>
      </div>
    </HubPageLayout>
  );
};

export default withSubscriptionContextProvider(withHeadTag(Subscriptions));
